// Discovery hub for besideU's auth surfaces. Fans out to the four entry
// points — tenant sign-in, HR self-serve signup, BesideU Admin, CGC member —
// so a visitor (or the marketing CTA) doesn't need to know which one they
// want. Each direct route (/sign-in, /sign-up, /admin/sign-in, /cgc/sign-in)
// remains independently linkable; this page is purely a fan-out.

import { Link, redirect } from 'react-router';
import { fetchMe, landingPathFor } from '@/lib/auth.ts';
import { Logo } from '@/components/brand/Logo.tsx';

export async function loginLoader() {
  const session = await fetchMe();
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  if (session) throw redirect(landingPathFor(session.role));
  return null;
}

export function Login() {
  return (
    <main className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center px-6 py-12">
      <Logo className="text-2xl" />
      <h1 className="mt-6 text-2xl font-semibold tracking-tight">Sign in to besideU</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Pick the option that fits. If you&rsquo;re not sure, you probably want the first.
      </p>

      <div className="mt-8 flex flex-col gap-3">
        <PrimaryCard
          to="/sign-in"
          title="Sign in to your organisation"
          description="For HR, Managers, and Employees with an account."
        />
        <Card
          to="/sign-up"
          title="Set up a new organisation"
          description="Create a workspace for your company. HR-led."
        />
        <details className="group rounded-lg border border-border bg-muted/30 p-4 open:bg-muted/50">
          <summary className="flex cursor-pointer list-none items-start justify-between gap-4">
            <span className="block">
              <span className="block font-medium">Operator or adviser sign-in</span>
              <span className="mt-1 block text-sm text-muted-foreground">
                For besideU staff and Clinical Governance Council members.
              </span>
            </span>
            <span
              aria-hidden="true"
              className="mt-1 text-sm text-muted-foreground transition-transform group-open:rotate-90"
            >
              ›
            </span>
          </summary>
          <div className="mt-4 flex flex-col gap-2 border-t border-border pt-4 text-sm">
            <Link
              to="/admin/sign-in"
              className="text-foreground underline underline-offset-2 hover:no-underline"
            >
              besideU Admin sign-in →
            </Link>
            <Link
              to="/cgc/sign-in"
              className="text-foreground underline underline-offset-2 hover:no-underline"
            >
              CGC member sign-in →
            </Link>
          </div>
        </details>
      </div>
    </main>
  );
}

function PrimaryCard({
  to,
  title,
  description,
}: {
  to: string;
  title: string;
  description: string;
}) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-primary bg-primary px-4 py-4 text-primary-foreground transition-opacity hover:opacity-90"
    >
      <div className="font-medium">{title}</div>
      <div className="mt-1 text-sm opacity-90">{description}</div>
    </Link>
  );
}

function Card({ to, title, description }: { to: string; title: string; description: string }) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-border bg-muted/30 px-4 py-4 transition-colors hover:bg-muted/60"
    >
      <div className="font-medium">{title}</div>
      <div className="mt-1 text-sm text-muted-foreground">{description}</div>
    </Link>
  );
}
